import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Eye, EyeSlash, ShieldCheck, Key, Clock, CheckCircle } from '@phosphor-icons/react';

export default function Register() {
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [masterCode, setMasterCode] = useState(''); // Código maestro para autorizar super usuario
  const [superuserCode, setSuperuserCode] = useState(''); // Código personal del super usuario
  const [confirmSuperuserCode, setConfirmSuperuserCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [registroExitoso, setRegistroExitoso] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!nombre || !email || !password || !confirmPassword) {
      toast.error('Por favor complete todos los campos');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    // Validate superuser codes if enabled
    if (isSuperuser) {
      if (!masterCode) {
        toast.error('Ingrese el código maestro para activar super usuario');
        return;
      }
      if (!superuserCode) {
        toast.error('Ingrese su código personal de super usuario');
        return;
      }
      if (superuserCode.length < 4) {
        toast.error('El código de super usuario debe tener al menos 4 caracteres');
        return;
      }
      if (superuserCode !== confirmSuperuserCode) {
        toast.error('Los códigos de super usuario no coinciden');
        return;
      }
    }

    setLoading(true);
    try {
      const response = await authAPI.register({
        email,
        password,
        nombre,
        is_superuser: isSuperuser,
        superuser_code: isSuperuser ? superuserCode : null,
        master_code: isSuperuser ? masterCode : null
      });
      
      // Check if user needs approval
      if (response.data.pendiente_aprobacion) {
        setRegistroExitoso(true);
      } else {
        toast.success('¡Cuenta creada exitosamente!');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cuenta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de registro exitoso - pendiente de aprobación
  if (registroExitoso) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_chicken-cost-tracker-1/artifacts/d7jt8vh8_LOGO%20ELANCO%20GROUP%20MARCA%20DE%20AGUA.png"
              alt="Elanco Group"
              className="h-24 mx-auto mb-4"
            />
          </div>

          <Card className="border-amber-200 shadow-lg bg-amber-50">
            <CardHeader className="space-y-1 pb-4 text-center">
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Clock size={32} className="text-amber-600" />
              </div>
              <CardTitle className="text-2xl text-amber-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                Registro Pendiente
              </CardTitle>
              <CardDescription className="text-amber-700">
                Su cuenta ha sido creada exitosamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-800">Registro completado</p>
                    <p className="text-sm text-slate-600 mt-1">
                      Su solicitud de registro está siendo revisada por un administrador.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-800">¿Qué sigue?</strong>
                  <br />
                  Un administrador revisará su solicitud y aprobará su acceso.
                  Recibirá acceso una vez que sea aprobado.
                </p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#E73425] hover:bg-[#D81C23]"
              >
                Volver a Iniciar Sesión
              </Button>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-500 mt-6">
            © 2024 Elanco Group. Todos los derechos reservados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_chicken-cost-tracker-1/artifacts/d7jt8vh8_LOGO%20ELANCO%20GROUP%20MARCA%20DE%20AGUA.png"
            alt="Elanco Group"
            className="h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold tracking-tight text-slate-800 uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Sistema de Gestión Avícola
          </h1>
        </div>

        <Card className="border-border/60 shadow-lg bg-white">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Crear Cuenta
            </CardTitle>
            <CardDescription className="text-center">
              Complete el formulario para registrarse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre Completo</Label>
                <Input
                  id="nombre"
                  type="text"
                  placeholder="Juan Pérez"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="input-focus"
                  data-testid="register-nombre"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="correo@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-focus"
                  data-testid="register-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-focus pr-10"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmar Contraseña</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-focus"
                  data-testid="register-confirm-password"
                />
              </div>
              
              {/* Super Usuario Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Checkbox
                    id="is-superuser"
                    checked={isSuperuser}
                    onCheckedChange={setIsSuperuser}
                    data-testid="register-is-superuser"
                  />
                  <Label htmlFor="is-superuser" className="flex items-center gap-2 cursor-pointer">
                    <ShieldCheck size={18} className="text-[#F69031]" />
                    Registrar como Super Usuario
                  </Label>
                </div>
                
                {isSuperuser && (
                  <div className="space-y-3 bg-amber-50 p-4 rounded-lg border border-amber-200 animate-fade-in">
                    <p className="text-xs text-amber-700">
                      Necesita el código maestro de autorización para registrarse como super usuario.
                    </p>
                    
                    {/* Código Maestro */}
                    <div className="space-y-2">
                      <Label htmlFor="master-code" className="flex items-center gap-1">
                        <Key size={14} className="text-amber-600" />
                        Código Maestro de Autorización
                      </Label>
                      <Input
                        id="master-code"
                        type="password"
                        placeholder="Código maestro"
                        value={masterCode}
                        onChange={(e) => setMasterCode(e.target.value)}
                        className="input-focus bg-white"
                        data-testid="register-master-code"
                      />
                    </div>
                    
                    <hr className="border-amber-200" />
                    
                    <p className="text-xs text-amber-700">
                      Cree su código personal de super usuario. Lo usará para operaciones sensibles.
                    </p>
                    
                    {/* Código Personal */}
                    <div className="space-y-2">
                      <Label htmlFor="superuser-code">Su Código Personal de Super Usuario</Label>
                      <Input
                        id="superuser-code"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Código secreto personal"
                        value={superuserCode}
                        onChange={(e) => setSuperuserCode(e.target.value)}
                        className="input-focus bg-white"
                        data-testid="register-superuser-code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-superuser-code">Confirmar Código Personal</Label>
                      <Input
                        id="confirm-superuser-code"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Confirme el código"
                        value={confirmSuperuserCode}
                        onChange={(e) => setConfirmSuperuserCode(e.target.value)}
                        className="input-focus bg-white"
                        data-testid="register-confirm-superuser-code"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full bg-[#E73425] hover:bg-[#D81C23] btn-animate"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <div className="spinner h-5 w-5" />
                ) : (
                  'Crear Cuenta'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">¿Ya tiene una cuenta? </span>
              <Link
                to="/login"
                className="text-[#E73425] hover:text-[#D81C23] font-medium"
                data-testid="login-link"
              >
                Iniciar Sesión
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2024 Elanco Group. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
